import { SpinnerGap } from '@phosphor-icons/react'
import clsx from 'clsx'
import type { LinkProps } from 'next/link'
import type { FC } from 'react'
import React from 'react'

import ButtonStyles from './Button.module.css'

export type IButtonSize = 'xxs' | 'xs' | 'sm' | 'md' | 'lg' | 'xl'

export type IButtonVariant =
  | 'default'
  | 'primary'
  | 'secondary'
  | 'outline'
  | 'link'
  | 'text'
  | 'naked'
  | 'warning'
  | 'success'
  | 'danger'
  | 'dashed'
  // | 'gradiantBorder'
  | 'Upgrade'
export interface ButtonProps extends React.HTMLAttributes<HTMLButtonElement> {
  block?: boolean
  className?: any
  children?: React.ReactNode
  disabled?: boolean
  onClick?: React.MouseEventHandler<HTMLButtonElement>
  icon?: React.ReactNode
  iconRight?: React.ReactNode
  loading?: boolean
  size?: IButtonSize
  variant?: IButtonVariant
  danger?: boolean
  type?: 'button' | 'submit' | 'reset'
  ref?: any
  fab?: boolean
  textAlign?: 'left' | 'center' | 'right'
  as?: keyof JSX.IntrinsicElements | FC<any>
  href?: string | LinkProps['href']
  rounded?: 'xxs' | 'xs' | 'sm' | 'md' | 'lg' | 'xl' | 'full'
  target?: string
  isResponsive?: boolean
  truncate?: boolean
  htmlFor?: string
  dataTestId?: string
}

interface CustomButtonProps extends React.HTMLAttributes<HTMLOrSVGElement> {}

const Button: FC<ButtonProps> = React.forwardRef<HTMLButtonElement>(
  (
    {
      block,
      className,
      children,
      danger,
      disabled = false,
      onClick,
      icon,
      iconRight,
      loading = false,
      size = 'sm',
      style,
      variant = 'primary',
      type,
      tabIndex,
      as,
      textAlign = 'center',
      rounded = 'md',
      fab,
      isResponsive = false,
      truncate = false,
      dataTestId,
      ...props
    }: ButtonProps,
    ref
  ) => {
    const classes = [ButtonStyles['hnui-btn']]
    const containerClasses = [ButtonStyles['hnui-btn-container']]

    classes.push(ButtonStyles[`hnui-btn-${variant}`])

    if (truncate) {
      classes.push('!overflow-hidden')
    }

    if (block) {
      containerClasses.push(ButtonStyles['hnui-btn--w-full'])
      classes.push(ButtonStyles['hnui-btn--w-full'])
    }

    if (size) {
      if (fab) classes.push(ButtonStyles[`hnui-btn-fab-${size}`])
      if (isResponsive)
        classes.push(ButtonStyles[`hnui-btn-responsive-${size}`])
      if (!fab && !isResponsive) classes.push(ButtonStyles[`hnui-btn--${size}`])
    }

    if (rounded) classes.push(ButtonStyles[`hnui-btn-rounded-${rounded}`])
    if (className) classes.push(className)
    if (danger) classes.push(ButtonStyles['hnui-btn-danger'])

    const iconLoaderClasses = [ButtonStyles['hnui-btn--anim--spin']]
    if (loading) iconLoaderClasses.push(ButtonStyles[`hnui-btn-loader--center`])

    classes.push(ButtonStyles[`hnui-btn--text-align-${textAlign}`])

    const CustomButton: React.FC<CustomButtonProps> = ({
      ...buttonProps
    }: CustomButtonProps) => {
      const Tag = as as keyof JSX.IntrinsicElements
      return <Tag {...buttonProps} />
    }

    const renderButtonChildren = () => {
      if (loading) {
        return <SpinnerGap size={12} className={iconLoaderClasses.join(' ')} />
      }
      if (!children) return <></>
      return (
        <span
          className={clsx(
            isResponsive ? 'hidden md:block' : '',
            truncate ? 'truncate' : ''
          )}
        >
          {children}
        </span>
      )
    }

    const RenderedButton = ({ children: _children }: any) => {
      if (as) {
        return (
          <CustomButton
            data-testid={dataTestId}
            className={clsx(
              classes.join(' '),
              disabled ? 'cursor-not-allowed decoration-current opacity-50' : ''
            )}
            onClick={onClick}
            style={style}
            {...props}
          >
            {_children}
          </CustomButton>
        )
      }
      return (
        <button
          {...props}
          data-testid={dataTestId}
          ref={ref}
          className={classes.join(' ')}
          disabled={loading || (disabled && true)}
          onClick={onClick}
          style={style}
          type={type}
          tabIndex={tabIndex}
        >
          {_children || <></>}
        </button>
      )
    }

    return (
      <RenderedButton data-testid={dataTestId}>
        {icon && !loading && icon}
        {renderButtonChildren()}
        {iconRight && !loading && iconRight}
      </RenderedButton>
    )
  }
)

Button.displayName = 'Button'

export default Button
